"use client";

import type { JSX } from "react";
import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { MenuIcon, XIcon } from "lucide-react";
import Link from "next/link";
import React, { Fragment, useState } from "react";
import { HipelifeLogo } from "./HipelifeLogo";
import { Container } from "./ui/Container";

interface NavigationItem {
  name: string;
  href: string;
}

interface TopMenuProps {
  navigation: NavigationItem[];
}

export default function TopMenu({ navigation }: TopMenuProps): JSX.Element {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="sticky top-0 z-50 bg-white md:bg-white/95 md:backdrop-blur-md border-b border-gray-200 shadow-sm transition-colors duration-200">
      <Container size="lg" padding="md">
        <div className="relative flex h-16 items-center justify-between">
          {/* Left: Logo */}
          <div className="flex items-center">
            <Link
              href="/"
              onClick={() => setMobileMenuOpen(false)}
              className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
            >
              <HipelifeLogo size={48} showText={true} />
            </Link>
          </div>

          {/* Center: Desktop navigation */}
          <nav className="absolute left-1/2 -translate-x-1/2 hidden md:flex md:gap-x-8" aria-label="Main navigation">
            {navigation.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className="text-sm font-medium text-gray-700 hover:text-indigo-600 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2 rounded-sm"
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <div className="absolute right-0 md:hidden">
            <button
              type="button"
              aria-label="Open main menu"
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(true)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-indigo-600 hover:bg-gray-100 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
            >
              <MenuIcon className="block h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </Container>

      {/* Full-screen Mobile Drawer */}
      <Transition show={mobileMenuOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[60] md:hidden" onClose={setMobileMenuOpen}>
          {/* Backdrop */}
          <TransitionChild
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />
          </TransitionChild>

          {/* Drawer panel */}
          <div className="fixed inset-0 flex justify-end">
            <TransitionChild
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <DialogPanel className="relative flex w-full max-w-xs flex-col overflow-y-auto bg-white shadow-xl">
                {/* Drawer header */}
                <div className="flex items-center justify-between border-b border-gray-100 px-4 py-4">
                  <Link
                    href="/"
                    onClick={() => setMobileMenuOpen(false)}
                    className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
                  >
                    <HipelifeLogo size={40} showText={true} />
                  </Link>
                  <button
                    type="button"
                    aria-label="Close menu"
                    onClick={() => setMobileMenuOpen(false)}
                    className="rounded-md p-2 text-gray-700 hover:bg-gray-100 hover:text-indigo-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
                  >
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {/* Navigation links */}
                <nav className="flex-1 px-2 py-6" aria-label="Mobile navigation">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      href={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center rounded-xl px-4 py-3.5 text-base font-medium text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
                    >
                      {item.name}
                    </Link>
                  ))}
                </nav>

                {/* Drawer footer */}
                <div className="border-t border-gray-100 px-4 py-5 safe-area-pb">
                  <p className="text-center text-xs text-gray-400">
                    Serious about fitness. Realistic about life.
                  </p>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>
    </nav>
  );
}
